import {
  HHBaseTable,
  HHChip,
  HHLink,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import dayjs from 'dayjs';
import { routes } from '../../constants/strings/routes';
import { useDynamicDrawerContext } from '../../context/dynamic-drawer.context';
import { BillsPaginatedType, useGetContractsV2Query } from '../../types';
import { formatCurrency } from '../../utils/currency-helpers';
import LoadingComponent from '../utils/loading';
import MoreDetailsButton from '../utils/more-details-button';
import BillDetails from './bill-details';

export interface AdminFeeBillsListProps {
  data?: BillsPaginatedType;
  loading: boolean;
}

const AdminFeeBillsList = ({
  data,
  loading,
}: AdminFeeBillsListProps): JSX.Element => {
  const { setDrawerContent } = useDynamicDrawerContext();

  const contractIds = [
    ...new Set(
      (data?.items ?? [])
        .map(bill => bill.contractId)
        .filter((id): id is number => id != null),
    ),
  ];
  const { data: contractsData } = useGetContractsV2Query({
    variables: { contractIds, includeVoided: true, page: 1, size: 100 },
    skip: contractIds.length === 0,
  });
  const contractById = new Map(
    (contractsData?.getContractsV2?.items ?? []).map(c => [c.id, c]),
  );

  if (loading) {
    return <LoadingComponent center />;
  }

  if (!data) {
    return (
      <HHTypography hhVariant="body">
        User admin fee bills not found
      </HHTypography>
    );
  }

  return (
    <HHStack spacing={1} direction="column">
      {data.items.length === 0 ? (
        <HHStack direction="row" padding={4} justifyContent="center">
          <HHTypography hhVariant="muted">No admin fee bills</HHTypography>
        </HHStack>
      ) : (
        <HHBaseTable
          headerRowBottomBorder="1px solid rgba(0, 0, 0, 0.12)"
          bodyRowBorder={{
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          }}
          bodyTypographyVariant="inherit"
          tableHeaders={[
            { value: 'Id', align: 'left' },
            { value: 'Date of Service', align: 'left' },
            { value: 'Client Insurer Id', align: 'left' },
            { value: 'Amount', align: 'left' },
            { value: 'Status', align: 'left' },
            { value: '', align: 'left' },
          ]}
          bodyContent={data.items.map(bill => {
            const contract =
              bill.contractId != null
                ? contractById.get(bill.contractId)
                : undefined;
            const clientsInsurerId = contract?.clientsInsurerId;
            const currency = contract?.currency ?? null;
            return {
              id: {
                value: bill.billId,
              },
              dateOfService: {
                value: dayjs(bill.dateOfService).format('L'),
              },
              clientInsurerId: {
                value:
                  clientsInsurerId != null ? (
                    <HHLink
                      href={`${routes.billing.main}/${clientsInsurerId}`}
                      target="_blank"
                    >
                      {clientsInsurerId}
                    </HHLink>
                  ) : (
                    '--'
                  ),
              },
              amount: {
                value: Number.isFinite(bill.billAmount)
                  ? formatCurrency(bill.billAmount as number, currency)
                  : '--',
              },
              status: {
                value: (
                  <HHChip
                    hhVariant="variant-bypass"
                    size="small"
                    label={bill.status.replace(/_/g, ' ')}
                  />
                ),
              },
              actions: {
                value: (
                  <MoreDetailsButton
                    onClick={(): void =>
                      setDrawerContent(
                        <BillDetails
                          key={bill.billId}
                          billId={bill.billId}
                          currency={currency}
                        />,
                        <HHTypography hhVariant="section-title">
                          Bill Data
                        </HHTypography>,
                      )
                    }
                  />
                ),
              },
            };
          })}
        />
      )}
    </HHStack>
  );
};

export default AdminFeeBillsList;
